import {
    formatDate_yyyyMMdd,
    getLogicalEndOfDay,
    getLogicalStartOfDay,
    logicalStartOfToday
} from '@cineamo/ui/src/helpers/date-helper';

// Sentinel selectedDate value for the PREVIEWS view ("Vorpremieren"): showings that
// happen strictly before the movie's release date. Release-day showings are regular
// showings and belong to that day's date button, not here.
export const PREVIEWS_SELECTED_DATE = 'previews';

// Value PREVIEWS_SELECTED_DATE had before the PREMIERES → PREVIEWS rename. Still accepted
// in the widget's public query params so embeds on sites we do not control keep working.
export const LEGACY_PREVIEWS_SELECTED_DATE = 'premieres';

export type InitiallySelectedDate = 'all' | 'today' | typeof PREVIEWS_SELECTED_DATE;

/**
 * True while the movie's release day still lies ahead. Only then can previews exist,
 * and only then do the date buttons start at the release date instead of today.
 * A movie releasing today counts as released — there is no "before release date" left.
 */
export const isBeforeReleaseDate = (releaseDate?: string): boolean =>
    !!releaseDate && getLogicalStartOfDay(releaseDate).getTime() > logicalStartOfToday().getTime();

/**
 * End of the previews window: the last moment of the logical day *before* the release
 * day, so a showing on the release date is never counted as a preview.
 */
export const getPreviewsEndDatetime = (releaseDate: string): Date => {
    const dayBeforeRelease = getLogicalStartOfDay(releaseDate);
    dayBeforeRelease.setDate(dayBeforeRelease.getDate() - 1);
    return getLogicalEndOfDay(dayBeforeRelease);
};

/**
 * First day the date buttons show: the release date while the movie is unreleased
 * (everything before it is covered by the PREVIEWS tab), today once it has released.
 */
export const getDateButtonsStartDate = (releaseDate?: string): Date =>
    isBeforeReleaseDate(releaseDate) ? getLogicalStartOfDay(releaseDate) : logicalStartOfToday();

/**
 * Maps the public `initiallySelectedDate` config value to the provider's selectedDate.
 * 'today' resolves to today's date string; 'all' and 'previews' pass through as sentinels.
 * If 'previews' turns out to be unavailable, DateFilter resets the selection to 'all'.
 *
 * Without an explicit value the release date decides: an unreleased movie opens on ALL
 * (TODAY is not among its date buttons at all), a released one on today.
 */
export const resolveInitialSelectedDate = (
    initiallySelectedDate?: InitiallySelectedDate,
    releaseDate?: string
): string => {
    if (initiallySelectedDate === 'all') {
        return 'all';
    }
    if (initiallySelectedDate === PREVIEWS_SELECTED_DATE) {
        return PREVIEWS_SELECTED_DATE;
    }
    if (initiallySelectedDate === undefined && isBeforeReleaseDate(releaseDate)) {
        return 'all';
    }
    return formatDate_yyyyMMdd(logicalStartOfToday());
};

/**
 * Validates the `initiallySelectedDate` query param, returning `undefined` for anything
 * unrecognised so the release-date-based default applies.
 */
export const parseInitiallySelectedDate = (value?: string | null): InitiallySelectedDate | undefined => {
    if (value === 'all' || value === 'today' || value === PREVIEWS_SELECTED_DATE) {
        return value;
    }
    if (value === LEGACY_PREVIEWS_SELECTED_DATE) {
        return PREVIEWS_SELECTED_DATE;
    }
    return undefined;
};
